import React, { useEffect, useState } from 'react';
import { authJsonHeaders } from '../utils/authHeaders';
import './ClientAddForm.css';

const TABS = [
  { id: 'cabinet', label: 'Cabinet', icon: 'fa-building' },
  { id: 'admin', label: 'Administration', icon: 'fa-headset' },
  { id: 'contact', label: 'Contact', icon: 'fa-user-tie' },
];

function emptyForm() {
  return {
    name: '',
    code: '',
    address: '',
    vat_code: '',
    email: '',
    phone: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_phone: '',
    contact_email: '',
  };
}

function formPayload(formData) {
  return {
    name: formData.name.trim(),
    code: formData.code.trim(),
    address: formData.address.trim(),
    vat_code: formData.vat_code.trim(),
    email: formData.email.trim(),
    phone: formData.phone.trim(),
    contact_first_name: formData.contact_first_name.trim(),
    contact_last_name: formData.contact_last_name.trim(),
    contact_phone: formData.contact_phone.trim(),
    contact_email: formData.contact_email.trim(),
  };
}

export default function CabinetEditForm({ cabinet = null, mode = 'edit', onSaved, onCancel }) {
  const isCreate = mode === 'create' || !cabinet?.id;
  const [activeTab, setActiveTab] = useState('cabinet');
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(!isCreate);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError('');
      setActiveTab('cabinet');
      if (isCreate) {
        setFormData(emptyForm());
        setLoadingData(false);
        return;
      }
      setLoadingData(true);
      setFormData({
        ...emptyForm(),
        name: cabinet.name || '',
        code: cabinet.code || '',
        address: cabinet.address || '',
        vat_code: cabinet.vat_code || '',
        email: cabinet.email || '',
        phone: cabinet.phone || '',
        contact_first_name: cabinet.contact_first_name || '',
        contact_last_name: cabinet.contact_last_name || '',
        contact_phone: cabinet.contact_phone || '',
        contact_email: cabinet.contact_email || '',
      });
      try {
        const res = await fetch(`/api/cabinets/${cabinet.id}`, {
          headers: authJsonHeaders(),
          cache: 'no-store',
        });
        const data = await res.json();
        if (!cancelled && res.ok && data.success && data.cabinet) {
          const c = data.cabinet;
          setFormData({
            name: c.name || '',
            code: c.code || '',
            address: c.address || '',
            vat_code: c.vat_code || '',
            email: c.email || '',
            phone: c.phone || '',
            contact_first_name: c.contact_first_name || '',
            contact_last_name: c.contact_last_name || '',
            contact_phone: c.contact_phone || '',
            contact_email: c.contact_email || '',
          });
        }
      } catch {
        /* keep list fields */
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [cabinet, isCreate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!formData.name.trim()) {
      setError('Nom du cabinet requis');
      setActiveTab('cabinet');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body = formPayload(formData);
      const res = await fetch(isCreate ? '/api/cabinets' : `/api/cabinets/${cabinet.id}`, {
        method: isCreate ? 'POST' : 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Enregistrement impossible');
      }
      if (typeof onSaved === 'function') onSaved(data.cabinet);
    } catch (err) {
      setError(err.message || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="caf">
      <form onSubmit={handleSubmit} noValidate>
        {error ? (
          <div className="caf-alert" role="alert">
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
            <span>{error}</span>
          </div>
        ) : null}

        <nav className="caf-tabs" role="tablist" aria-label="Sections du cabinet">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`caf-tab${activeTab === tab.id ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fas ${tab.icon}`} aria-hidden="true" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="caf-panel" role="tabpanel">
          {loadingData ? (
            <p className="caf-loading">Chargement…</p>
          ) : (
            <>
              {activeTab === 'cabinet' ? (
                <div className="caf-grid">
                  <div className="caf-field caf-field--name">
                    <label htmlFor="cab-edit-name">Nom</label>
                    <input
                      id="cab-edit-name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Nom du cabinet"
                      autoFocus
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cab-edit-code">Code</label>
                    <input
                      id="cab-edit-code"
                      name="code"
                      type="text"
                      value={formData.code}
                      onChange={handleChange}
                      placeholder="ex. CGM2"
                      disabled={loading}
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cab-edit-vat">Matricule fiscal</label>
                    <input
                      id="cab-edit-vat"
                      name="vat_code"
                      type="text"
                      value={formData.vat_code}
                      onChange={handleChange}
                      placeholder="Matricule fiscal"
                      disabled={loading}
                    />
                  </div>
                  <div className="caf-field caf-field--full">
                    <label htmlFor="cab-edit-address">Adresse</label>
                    <input
                      id="cab-edit-address"
                      name="address"
                      type="text"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Adresse du cabinet"
                      disabled={loading}
                    />
                  </div>
                </div>
              ) : null}

              {activeTab === 'admin' ? (
                <div className="caf-grid">
                  <div className="caf-field">
                    <label htmlFor="cab-edit-email">E-mail</label>
                    <input
                      id="cab-edit-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@cabinet.tn"
                      disabled={loading}
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cab-edit-phone">Téléphone</label>
                    <input
                      id="cab-edit-phone"
                      name="phone"
                      type="text"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Téléphone"
                      disabled={loading}
                    />
                  </div>
                </div>
              ) : null}

              {activeTab === 'contact' ? (
                <div className="caf-grid">
                  <div className="caf-field">
                    <label htmlFor="cab-edit-cfn">Prénom</label>
                    <input
                      id="cab-edit-cfn"
                      name="contact_first_name"
                      type="text"
                      value={formData.contact_first_name}
                      onChange={handleChange}
                      placeholder="Prénom"
                      disabled={loading}
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cab-edit-cln">Nom</label>
                    <input
                      id="cab-edit-cln"
                      name="contact_last_name"
                      type="text"
                      value={formData.contact_last_name}
                      onChange={handleChange}
                      placeholder="Nom"
                      disabled={loading}
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cab-edit-cphone">Téléphone</label>
                    <input
                      id="cab-edit-cphone"
                      name="contact_phone"
                      type="text"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      placeholder="Téléphone contact"
                      disabled={loading}
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cab-edit-cemail">E-mail</label>
                    <input
                      id="cab-edit-cemail"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      placeholder="contact@cabinet.tn"
                      disabled={loading}
                    />
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="caf-actions">
          <button type="button" className="caf-btn caf-btn--ghost" onClick={onCancel} disabled={loading}>
            Annuler
          </button>
          <button type="submit" className="caf-btn caf-btn--primary" disabled={loading || loadingData}>
            {loading ? 'Enregistrement…' : isCreate ? 'Créer' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
}
